import React, {
  memo,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import InfoBubble from '@gp/components/InfoBubble';
import InDevelopmentCard from './components/InDevelopmentCard';
import CommonContainer from '../components/CommonContainer';
import { useRequest } from 'ahooks';
import { fetchInDevelopmentData, inDevelopmentDownLoad } from './services';
import { fetchProjectFilterData } from '@gp/pages/StudioMonthlyReport/PerformanceOverview/services';
import { reportOperationLog } from '@gp/services/global';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import { getArraySort, toFirstUpperCase } from '@gp/utils/common';
import { getTableRecords, getTableOptions } from './utils';
import { SORT_REQ_MAP } from './constants';
import { SortBy } from 'tea-component/lib/table/addons';
import { TableListOptionsType } from '@gp/components/GeneralTable/index.d';
import { InDevelopmentDataType } from './types/services';
import './index.scss';
import TipContext from '@/pages/home/components/TipContext';
import { CONSTANT_TEXTS } from '@gp/constants/const';

const InDevelopment: React.FC<unknown> = () => {
  // 头部显示的时间
  const [headerDate, setHeaderDate] = useState<string>('');
  // 控制头部时间只取一次
  const isFinishHeaderDate = useRef<boolean>(false);
  // 表格展示宽度大于1350px时视为大屏
  const [isBigScreen, setIsBigScreen] = useState<boolean>(false);
  // 通知Project筛选器刷新下拉项及重新全选
  const [projectFilterRefreshRenderKey, setProjectFilterRefreshRenderKey] =
    useState<number>(0);
  // studio全选时project筛选器的下拉项
  const allProjectFilterOptionsRef = useRef<
    {
      value: string;
      text: string;
      label: string;
      groupKey: string;
    }[]
  >([]);
  const [projectFilterOptions, setProjectFilterOptions] = useState<
    {
      value: string;
      text: string;
      label: string;
      groupKey: string;
    }[]
  >([]);
  const paramsListRef = useRef<{
    select_id: string[];
    order: 'desc' | 'asc' | undefined;
    order_metric: string;
  }>({
    select_id: [],
    order: 'desc',
    order_metric: 'Total Dev Cost Forecast',
  });
  const studioFilterOptionsRef = useRef<
    {
      value: string;
      text: string;
      projects: {
        id: string;
        name: string;
      }[];
      [keyName: string]: any;
    }[]
  >([]);

  const { loading: filterLoading, data: filterData } = useRequest(
    fetchProjectFilterData,
    {
      defaultParams: [
        {
          page: 'in_development',
        },
      ],
    },
  );
  const {
    loading: inDevelopmentLoading,
    data: inDevelopmentData,
    run: inDevelopmentRun,
  } = useRequest(fetchInDevelopmentData, { manual: true });

  const studioFilterOptions = useMemo(() => {
    const studioList = filterData?.list;
    let newProjectOptions: {
      id: string;
      name: string;
    }[] = [];
    if (!studioList || !Array.isArray(studioList)) {
      studioFilterOptionsRef.current = [];
      return [];
    }
    const _options =
      getArraySort(studioList, 'string', 'asc', 'name')?.map((t) => {
        const currentItemProjects = t?.projects || [];
        newProjectOptions = [...newProjectOptions, ...currentItemProjects];
        return {
          value: t?.id || '',
          text: t?.name || '-',
          label: t?.name || '-',
          projects: t?.projects,
          groupKey: 'separated',
        };
      }) || [];

    setProjectFilterRefreshRenderKey(Date.now());
    const allProjectOptions =
      getArraySort(newProjectOptions, 'string', 'asc', 'name')?.map(
        (t: { name: string; id: string }) => {
          return {
            value: t?.id || '',
            text: t?.name || '-',
            label: t?.name || '-',
            groupKey: 'separated',
          };
        },
      ) || [];
    setProjectFilterOptions(allProjectOptions);
    allProjectFilterOptionsRef.current = allProjectOptions;

    studioFilterOptionsRef.current = _options;
    return _options;
  }, [filterLoading]);

  const filterOptions = useMemo(() => {
    return [
      {
        refreshRenderKey: projectFilterRefreshRenderKey,
        type: 'multipleselect',
        fieldName: 'project',
        value: [],
        option: projectFilterOptions,
        className: 'dbg-in-development-card-filter-project-selector',
        style: { marginRight: 10 },
        label: 'Project',
        disabled: filterLoading,
        placeholder: 'Please select Project',
      },
      {
        type: 'multipleselect',
        fieldName: 'studio',
        value: [],
        option: studioFilterOptions,
        className: 'dbg-in-development-card-filter-studio-selector',
        style: { marginRight: 10 },
        // listWidth: 260,
        label: 'Studio',
        disabled: filterLoading,
        placeholder: 'Please select Studio',
      },
    ];
  }, [
    filterLoading,
    studioFilterOptions,
    projectFilterOptions,
    projectFilterRefreshRenderKey,
  ]);

  const tableOptions: TableListOptionsType = useMemo(() => {
    return getTableOptions(
      inDevelopmentLoading,
      inDevelopmentData as InDevelopmentDataType,
      isBigScreen,
    );
  }, [inDevelopmentLoading, isBigScreen]);

  const tableRecords = useMemo(() => {
    return getTableRecords(
      inDevelopmentLoading,
      inDevelopmentData as InDevelopmentDataType,
    );
  }, [inDevelopmentLoading]);

  const handleTableSort = useCallback((v: SortBy<any>) => {
    paramsListRef.current = {
      ...paramsListRef.current,
      order: v?.order,
      order_metric: SORT_REQ_MAP?.[v?.by],
    };
    inDevelopmentRun(paramsListRef.current);
  }, []);

  const handleFilterChange = useCallback(
    (studio: string[], project: string[], handleFilterName: string) => {
      let selectId: string[] = [],
        newProjectOptions: {
          id: string;
          name: string;
        }[] = [];
      if (handleFilterName === 'project') {
        selectId = project;
        // studio筛选器变化时需要改变project筛选器的筛选项，并且project筛选器要重新全选
      } else if (handleFilterName === 'studio') {
        // 全选
        if (!studio?.length) {
          setProjectFilterRefreshRenderKey(Date.now());
          setProjectFilterOptions(allProjectFilterOptionsRef.current);
        } else {
          for (let i = 0, l = studio?.length; i < l; i++) {
            const t = studio?.[i];
            const studioFilterOptions = studioFilterOptionsRef.current;
            if (Array.isArray(studioFilterOptions)) {
              for (let j = 0, l = studioFilterOptions?.length; j < l; j++) {
                const k = studioFilterOptions?.[j];
                if (k?.value === t && Array.isArray(k?.projects)) {
                  const currentItemProjects = k?.projects || [];
                  const currentItemProjectIds =
                    currentItemProjects?.map((item) => item?.id) || [];
                  newProjectOptions = [
                    ...newProjectOptions,
                    ...currentItemProjects,
                  ];
                  selectId = [...selectId, ...currentItemProjectIds];
                }
              }
            }
            if (i === l - 1) {
              setProjectFilterRefreshRenderKey(Date.now());
              setProjectFilterOptions(
                getArraySort(newProjectOptions, 'string', 'asc', 'name')?.map(
                  (t: { name: string; id: string }) => {
                    return {
                      value: t?.id || '',
                      text: t?.name || '-',
                      label: t?.name || '-',
                      groupKey: 'separated',
                    };
                  },
                ) || [],
              );
            }
          }
        }
      } else {
        selectId = paramsListRef?.current?.select_id;
      }
      paramsListRef.current = {
        ...paramsListRef.current,
        select_id: selectId,
      };
      inDevelopmentRun(paramsListRef.current);
    },
    [],
  );

  const handleScreenResize = useCallback(() => {
    // 表格展示宽度大于1350px时视为大屏，此时区间展示列宽度变为原来的两倍
    const t = setTimeout(() => {
      const tableW =
        document.querySelector('.dbg-studio-monthly-report-in-development-list')
          ?.clientWidth || 0;
      setIsBigScreen(tableW > 1350);
      clearTimeout(t);
    }, 50);
  }, []);

  // 初始化请求数据
  useEffect(() => {
    inDevelopmentRun(paramsListRef.current);
    // 页面打点
    reportOperationLog(
      ALL_REPORT_OPERATION_LOG['STUDIO_MONTHLY_REPORT_IN_DEVELOPMENT'],
    );
    // 屏幕尺寸变化适配表格区间展示列的宽度
    window.addEventListener('resize', handleScreenResize);
    handleScreenResize();

    return () => {
      window.removeEventListener('resize', handleScreenResize);
    };
  }, []);

  // 组织头部显示的时间
  useEffect(() => {
    if (isFinishHeaderDate.current || inDevelopmentLoading) return;
    const date = inDevelopmentData?.date_time || '';
    if (date) {
      setHeaderDate(date);
      isFinishHeaderDate.current = true;
    }
  }, [inDevelopmentLoading]);

  return (
    <CommonContainer
      loading={false}
      title={'In-Development Title'}
      subTitle={`Latest Data: ${headerDate || '-'}`}
    >
      <div className="dbg-studio-monthly-report-in-development">
        <InDevelopmentCard
          title={
            <div className="dbg-studio-monthly-report-card-title">
              In-Development Title
              <TipContext
                AWidth={555}
                placement="right"
                tipText={
                  <>
                    <p>【Metrics】: Total Dev Cost、Basic Info、Milestone</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Gate Review Documents</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: By Milestone</p>
                    <br />
                    <p>【Metrics】: Profit Ratio & Staff Rate</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Calculate</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Quarterly</p>
                    <br />
                    <p>【Metrics】: HC</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Rolling Forecast</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Quarterly</p>
                  </>
                }
                className="globalpedia-modal-font14 dbg-studio-monthly-report-card-title-info"
              />
              {/* <InfoBubble
                className="globalpedia-modal-font14 gp-studio-monthly-report-card-title-info"
                style={{
                  lineHeight: '16px',
                }}
                placement="right-start"
                customBubbleContent={
                  <>
                    <p>【Metrics】: Total Dev Cost、Basic info、Milestone</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Gate review documents</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: By Milestone</p>
                    <br />
                    <p>【Metrics】: Profit Ratio & Staff  Rate</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: calculate</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Quarterly</p>
                    <br />
                    <p>【Metrics】: HC</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Rolling forecast</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Quarterly</p>
                  </>
                }
              /> */}
            </div>
          }
          filterLoading={filterLoading}
          filterOptions={filterOptions}
          filterChange={handleFilterChange}
          tableClassName="dbg-studio-monthly-report-in-development-list dbg-table-sticky"
          reRenderTable={isBigScreen}
          tableLoading={inDevelopmentLoading}
          tableOptions={tableOptions}
          tableRecords={tableRecords}
          onTableSort={handleTableSort}
          uniqueDownloadId="studio_monthly_report_live_title_download"
          downloadParams={paramsListRef.current}
          downloadServices={inDevelopmentDownLoad}
          bordered={false}
          downloadLogKey={{
            csv: 'STUDIO_MONTHLY_REPORT_IN_DEVELOPMENT_CSV_DOWNLOAD_SUBMIT',
            excel: 'STUDIO_MONTHLY_REPORT_IN_DEVELOPMENT_EXCEL_DOWNLOAD_SUBMIT',
          }}
        />
      </div>
    </CommonContainer>
  );
};

export default memo(InDevelopment);
